import { GetProfileWeeklySummaryQuery } from "types/graphql-schema";

import IntegrationLogo from "@components/integration-logo/integration-logo";
import AppLink from "@components/link/link";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";

import { getSummarySourceIcon } from "./summary-icons";

type SummarySource = NonNullable<
  GetProfileWeeklySummaryQuery["profileWeeklySummary"]
>["insights"][number]["sources"][number];

// Convert an absolute backend URL to an in-app relative path. Returns null if
// the URL is empty or can't be parsed, so the caller falls back to plain text.
const toRelativePath = (url: string | null | undefined): string | null => {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return parsed.pathname + parsed.search;
  } catch {
    return null;
  }
};

const CHIP_CLASS =
  "inline-flex items-center gap-1 rounded-sm bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600 max-w-[220px]";

// A small source chip shown under a summary line — an icon + label linking out
// to the underlying artifact/meeting/issue (internal in-app, external new tab).
const SummarySourceChip = ({ source }: { source: SummarySource }) => {
  const label = source.title || source.type;
  // External events (github/linear/jira/hubspot/youtrack/…) show their provider
  // logo; internal Topicflow sources (meeting/goal/feedback/…) use a TopicflowIcon.
  const inner = (
    <>
      {source.isExternal ? (
        <IntegrationLogo
          provider={source.provider}
          size={3.5}
          className="rounded-[2px]"
        />
      ) : (
        <TopicflowIcon
          type={getSummarySourceIcon(source)}
          className="h-3.5 w-3.5 shrink-0 text-gray-400"
        />
      )}
      <span className="min-w-0 truncate">{label}</span>
    </>
  );

  if (source.isExternal && source.url) {
    return (
      <a
        href={source.url}
        target="_blank"
        rel="noreferrer"
        className={classNames(CHIP_CLASS, "hover:bg-gray-200")}
      >
        {inner}
      </a>
    );
  }

  const relativePath = toRelativePath(source.url);
  if (relativePath) {
    return (
      <AppLink
        to={relativePath}
        className={classNames(CHIP_CLASS, "hover:bg-gray-200")}
      >
        {inner}
      </AppLink>
    );
  }

  return <span className={CHIP_CLASS}>{inner}</span>;
};

export default SummarySourceChip;
